/**
 * Bloque "Complementos con toggle" (snippets/pairs-addons.liquid)
 *
 * 1. Carga los productos complementarios de Search & Discovery (/recommendations/products con la sección de producto)
 *    y se queda con el contenido de este bloque.
 * 2. Preselecciona en cada complemento la variante que más se parece a la elegida arriba (misma talla / color).
 * 3. Suma los complementos activados al precio del botón "Añadir al carrito".
 * 4. Cuando el producto se añade al carrito (evento variant:add del formulario, también con set o por cantidad),
 *    añade los complementos activados con la propiedad _pack_discount para que la función de descuento aplique el %.
 */
(() => {
  if (window.customElements.get("pairs-addons")) return;

  const moneyFormat = () =>
    (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || "{{amount}}";

  // Mismo formato que el filtro "money" de Liquid
  function formatMoney(cents) {
    const value = Math.round(Number(cents || 0));
    const withDelimiters = (decimals, thousands, decimal) => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split(".");
      const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fraction ? `${grouped}${decimal}${fraction}` : grouped;
    };
    const html = moneyFormat().replace(/\{\{\s*(\w+)\s*\}\}/, (_match, key) => {
      switch (key) {
        case "amount_no_decimals": return withDelimiters(0, ",", ".");
        case "amount_with_comma_separator": return withDelimiters(2, ".", ",");
        case "amount_no_decimals_with_comma_separator": return withDelimiters(0, ".", ",");
        case "amount_with_apostrophe_separator": return withDelimiters(2, "'", ".");
        case "amount_with_space_separator": return withDelimiters(2, " ", ",");
        default: return withDelimiters(2, ",", ".");
      }
    });
    const holder = document.createElement("div");
    holder.innerHTML = html;
    return holder.textContent;
  }

  // Inverso de formatMoney para el precio que ya muestra el botón (lo cambian la variante, el set o la cantidad)
  function parseMoney(text) {
    const digits = String(text || "").replace(/[^\d]/g, "");
    if (!digits) return 0;
    return /no_decimals/.test(moneyFormat()) ? Number(digits) * 100 : Number(digits);
  }

  // Precio del botón = precio que muestra el tema (variante, set o cantidad) + todos los extras de la ficha
  // (elementos con data-atc-extra: complementos activados, liner…). Compartido con assets/liner-option.js.
  if (!window.velitesUpdateAtcPrice) {
    window.velitesUpdateAtcPrice = (section) => {
      const extra = Array.from(section.querySelectorAll("[data-atc-extra]"))
        .reduce((sum, element) => sum + (Number(element.dataset.atcExtra) || 0), 0);
      section.querySelectorAll("[data-atc-price]").forEach((price) => {
        const shown = price.textContent;
        if (price.dataset.pairsLast !== shown) price.dataset.pairsBase = String(parseMoney(shown));
        const next = formatMoney(Number(price.dataset.pairsBase || 0) + extra);
        if (next !== shown) price.textContent = next;
        price.dataset.pairsLast = next;
      });
      return extra;
    };
    window.velitesFormatMoney = formatMoney;
  }

  class PairsAddons extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.discount = parseInt(this.dataset.discount, 10) || 0;
      this.section = this.closest(".shopify-section") || document;

      if (this.hasAttribute("data-load")) {
        this.load();
      } else {
        this.setup();
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      if (this._onVariantAdd) document.removeEventListener("variant:add", this._onVariantAdd);
      this._connected = false;
    }

    async load() {
      const sectionId = (this.section.id || "").replace("shopify-section-", "");
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      try {
        const response = await fetch(
          `${root}recommendations/products?product_id=${this.dataset.productId}&limit=${this.dataset.limit}&section_id=${sectionId}&intent=complementary`,
          { priority: "low" },
        );
        const doc = new DOMParser().parseFromString(await response.text(), "text/html");
        const fresh = doc.querySelector(`pairs-addons[data-block-id="${this.dataset.blockId}"]`);
        if (fresh && fresh.querySelector("[data-addon]")) {
          this.replaceChildren(...document.importNode(fresh, true).childNodes);
          this.removeAttribute("data-load");
          this.setup();
          return;
        }
      } catch (_error) {
        // sin recomendaciones: se oculta el bloque
      }
      (this.closest(".product-info__block-item") || this).remove();
    }

    setup() {
      this.addons = Array.from(this.querySelectorAll("[data-addon]"));
      if (!this.addons.length) return;

      this.addons.forEach((addon) => {
        const variantInput = addon.querySelector("[data-addon-variant]");
        const toggle = addon.querySelector("[data-addon-toggle]");
        if (variantInput.tagName === "SELECT") {
          this.preselectVariant(variantInput);
          variantInput.addEventListener("change", () => {
            this.renderAddonPrice(addon);
            this.updateButtonPrice();
          });
        }
        this.renderAddonPrice(addon);
        toggle.addEventListener("change", () => this.updateButtonPrice());
      });

      // El precio del botón lo vuelven a escribir el selector de variantes, el set o la compra por cantidad:
      // en ese momento se vuelve a sumar lo de los complementos
      this._observer = new MutationObserver(() => {
        if (this._writing) return;
        const extras = Array.from(this.section.querySelectorAll("[data-atc-extra]")).some((element) => Number(element.dataset.atcExtra) > 0);
        if (extras) this.updateButtonPrice();
      });
      this._observer.observe(this.section, { childList: true, subtree: true, characterData: true });

      this._onVariantAdd = (event) => this.onVariantAdd(event);
      document.addEventListener("variant:add", this._onVariantAdd);
    }

    // Variante del complemento con más opciones en común con lo elegido arriba (talla, color…) y en stock
    preselectVariant(select) {
      const chosen = new Set(
        Array.from(this.section.querySelectorAll("input[data-option-position]:checked, select[data-option-position]"))
          .map((input) => {
            const label = input.id ? this.section.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
            return String((label && label.textContent) || input.value || "").trim().toLowerCase();
          })
          .filter(Boolean),
      );
      if (!chosen.size) return;

      let best = null;
      let bestScore = 0;
      Array.from(select.options).forEach((option) => {
        if (option.disabled) return;
        let options = [];
        try {
          options = JSON.parse(option.dataset.options || "[]");
        } catch (_error) {
          options = [];
        }
        const score = options.filter((value) => chosen.has(String(value).trim().toLowerCase())).length;
        if (score > bestScore) {
          best = option;
          bestScore = score;
        }
      });
      if (best) select.value = best.value;
    }

    variantOf(addon) {
      const input = addon.querySelector("[data-addon-variant]");
      if (input.tagName === "SELECT") {
        const option = input.options[input.selectedIndex];
        return { id: input.value, price: Number(option?.dataset.price || 0), image: option?.dataset.image || "" };
      }
      return { id: input.value, price: Number(input.dataset.price || 0), image: "" };
    }

    // Igual que la función de descuento del carrito: el descuento se redondea hacia abajo (61,99 € -10% = 55,80 €)
    discounted(cents) {
      return cents - Math.floor((cents * this.discount) / 100);
    }

    renderAddonPrice(addon) {
      const variant = this.variantOf(addon);
      const current = addon.querySelector("[data-addon-price]");
      const compare = addon.querySelector("[data-addon-compare]");
      if (current) current.textContent = formatMoney(this.discounted(variant.price));
      if (compare) compare.textContent = formatMoney(variant.price);
      const image = addon.querySelector("[data-addon-image]");
      if (image && variant.image) {
        image.removeAttribute("srcset");
        image.src = variant.image;
      }
    }

    selectedAddons() {
      return this.addons.filter((addon) => addon.querySelector("[data-addon-toggle]").checked);
    }

    selectedCents() {
      return this.selectedAddons().reduce((sum, addon) => sum + this.discounted(this.variantOf(addon).price), 0);
    }

    // Precio del botón = precio del producto (variante, set o cantidad) + complementos activados
    updateButtonPrice() {
      this.dataset.atcExtra = String(this.selectedCents());
      this._writing = true;
      window.velitesUpdateAtcPrice(this.section);
      // Las mutaciones se entregan en una microtarea: se ignoran las provocadas por esta escritura
      queueMicrotask(() => {
        this._writing = false;
      });
    }

    async onVariantAdd(event) {
      const form = event.target && event.target.closest ? event.target.closest("form") : null;
      // getAttribute: form.id devolvería el campo name="id" (la variante) del propio formulario
      if (!form || form.getAttribute("id") !== this.dataset.formId) return;

      const selected = this.selectedAddons();
      if (!selected.length) return;

      const items = selected.map((addon) => ({
        id: Number(this.variantOf(addon).id),
        quantity: 1,
        properties: this.discount > 0 ? { _pack_discount: String(this.discount) } : {},
      }));

      // Ya añadidos: se desactivan para no repetirlos en la siguiente compra
      selected.forEach((addon) => {
        addon.querySelector("[data-addon-toggle]").checked = false;
      });
      this.updateButtonPrice();

      try {
        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
        await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items }),
        });
      } finally {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
      }
    }
  }

  window.customElements.define("pairs-addons", PairsAddons);
})();
